import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Chip,
  Avatar,
  Link as MuiLink,
  Button,
  Paper,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import DataObjectIcon from "@mui/icons-material/DataObject";
import CodeIcon from "@mui/icons-material/Code";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DataUsageIcon from "@mui/icons-material/DataUsage";
import ClassIcon from "@mui/icons-material/Class";
import TuneIcon from "@mui/icons-material/Tune";
import BalanceIcon from "@mui/icons-material/Balance";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import VideocamIcon from "@mui/icons-material/Videocam";
import SummarizeIcon from "@mui/icons-material/Summarize";
import SensorOccupiedIcon from "@mui/icons-material/SensorOccupied";
import QuizIcon from "@mui/icons-material/Quiz";
import ShieldIcon from "@mui/icons-material/Shield";
import LockIcon from "@mui/icons-material/Lock";
import GavelIcon from "@mui/icons-material/Gavel";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import { getImageUrl } from "../../../utils/helpers";

const labelToLogo = {
  "AWS SageMaker": getImageUrl("aws_logo2.jpg"),
  Doccano: getImageUrl("doccano_logo.png"),
  Labelbox: getImageUrl("labelbox_logo.jpg"),
  LightTag: getImageUrl("lighttag_logo.png"),
  SuperAnnotate: getImageUrl("superannotate_logo.png"),
  "Label Studio": getImageUrl("label_studio_logo.jpg"),
};

const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

const AiDataAnnotation = ({ baseConfig }) => {
  const { priColor, priHoverColor, tertColor, tertHoverColor } =
    baseConfig.siteBranding;

  // Data types for annotation
  const dataTypes = [
    { label: "Document", icon: <AssignmentIcon /> },
    { label: "Text", icon: <TextFieldsIcon /> },
    { label: "Audio", icon: <GraphicEqIcon /> },
    { label: "Video", icon: <VideocamIcon /> },
  ];

  // Labeling types
  const labelingTypes = [
    { label: "Classification", icon: <ClassIcon /> },
    { label: "Data Collection", icon: <DataUsageIcon /> },
    { label: "Fine-tuning", icon: <TuneIcon /> },
    { label: "Prompt + Response Writing", icon: <AssignmentTurnedInIcon /> },
    { label: "Legal Text Summarizing", icon: <SummarizeIcon /> },
    { label: "RLHF", icon: <SensorOccupiedIcon /> },
    { label: "Evaluation", icon: <QuizIcon /> },
  ];

  // Security features
  const securityFeatures = [
    { label: "Attorney-Client Privilege", icon: <GavelIcon /> },
    { label: "Attorney Work-Product", icon: <ShieldIcon /> },
    { label: "Ethical Duty of Confidentiality", icon: <LockIcon /> },
  ];

  return (
    <Box sx={{ mb: 6 }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          backgroundColor: `${tertColor}22`,
          borderRadius: "4px 4px 0 0",
        }}
      >
        <DataObjectIcon sx={{ mr: 2, color: tertColor }} />
        <Typography variant="h5" fontWeight="bold">
          Legal Data Annotation and Prompt Engineering for Complex Legal
          Reasoning
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${tertColor}33`,
          borderTop: 0,
          borderRadius: "0 0 4px 4px",
          mb: 4,
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h6"
              gutterBottom
              fontWeight="bold"
              color={tertColor}
            >
              Overview of Our AI Data Annotation Services
            </Typography>
            <Typography variant="body1" paragraph>
              Kama Thuo, PLLC supports clients developing sophisticated
              AI-powered legal analysis tools, with a particular focus on patent
              law and complex technology transactions. Our services include
              designing advanced legal reasoning prompts for AI model training
              and fine-tuning, engineering accurate and contextually relevant
              training datasets, and developing robust scoring methodologies to
              measure and refine AI model performance.
            </Typography>

            <Typography variant="body1" paragraph>
              As a U.S. licensed law firm with an engineering & technology law
              practice focus, we've become adept at annotating data to train AI
              models for legal applications. In addition to U.S.-based staff, we
              have highly-trained and tech-savvy staff in Nairobi for
              budget-friendly labeling tasks, including staff with engineering
              and science degrees from accredited national universities.
            </Typography>

            <Typography variant="body1" paragraph>
              We closely collaborate with our AI Automation vendor{" "}
              <MuiLink href="https://ai.rfwel.com/" target="_blank">
                Rfwel Engineering, LLC
              </MuiLink>{" "}
              for more nuanced AI support. As a law firm, we uniquely provide
              attorney-client privilege, attorney work-product protections, and
              strict confidentiality standards, particularly valuable when AI
              data annotation tasks relate to litigation preparation or legal
              opinion formulation.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={getImageUrl("kth-head-2-ai.png")}
              alt="AI Data Annotation Services"
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                mb: 3,
              }}
            />
            <Box sx={{ mt: 3 }}>
              <Button
                startIcon={<LaunchIcon />}
                variant="contained"
                sx={{
                  backgroundColor: priColor,
                  color: "#fff",
                  "&:hover": { color: "#fff", backgroundColor: priHoverColor },
                }}
                component={RouterLink}
                to="/news-insights?category=ai-labeling"
              >
                Learn More about AI Data Annotation
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Card
              elevation={2}
              sx={{ borderRadius: 2, border: `1px solid ${tertColor}33` }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight="bold"
                  color={tertColor}
                >
                  What Sets Our Legal Annotation Services Apart
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: "100%",
                        backgroundColor: "#f9f9f9",
                        border: "1px solid #e0e0e0",
                        borderLeft: `4px solid ${tertColor}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        gutterBottom
                      >
                        Legal Domain Expertise
                      </Typography>
                      <Typography variant="body2">
                        Our annotation teams include legal professionals with
                        specialized knowledge in patent law, technology
                        transactions, and IP protection.
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: "100%",
                        backgroundColor: "#f9f9f9",
                        border: "1px solid #e0e0e0",
                        borderLeft: `4px solid ${tertColor}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        gutterBottom
                      >
                        Legal Protections
                      </Typography>
                      <Typography variant="body2">
                        As a law firm, we provide attorney-client privilege and
                        work product protections that typical annotation vendors
                        cannot offer.
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: "100%",
                        backgroundColor: "#f9f9f9",
                        border: "1px solid #e0e0e0",
                        borderLeft: `4px solid ${tertColor}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        gutterBottom
                      >
                        Complex Legal Reasoning
                      </Typography>
                      <Typography variant="body2">
                        Specialized in prompt engineering for sophisticated
                        legal analysis and reasoning tasks beyond simple
                        classification.
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: "100%",
                        backgroundColor: "#f9f9f9",
                        border: "1px solid #e0e0e0",
                        borderLeft: `4px solid ${tertColor}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        gutterBottom
                      >
                        Hybrid Staffing Model
                      </Typography>
                      <Typography variant="body2">
                        U.S.-based legal experts combined with skilled technical
                        staff in Nairobi provides both quality and
                        cost-effectiveness.
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h6"
              gutterBottom
              fontWeight="bold"
              color={tertColor}
            >
              Our Data Annotation Technical Capabilities
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ color: tertColor }}
              >
                Example Labeling Software
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {Object.keys(labelToLogo).map((label) => (
                  <Chip
                    key={label}
                    label={label}
                    avatar={<Avatar src={labelToLogo[label]} />}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ color: tertColor }}
              >
                Data Types We Work With
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {dataTypes.map((type, index) => (
                  <Chip
                    key={index}
                    label={type.label}
                    icon={type.icon}
                    sx={{ backgroundColor: `${tertColor}22` }}
                  />
                ))}
              </Box>
            </Box>

            <Box>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ color: tertColor }}
              >
                Labeling Types & Services
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {labelingTypes.map((type, index) => (
                  <Chip
                    key={index}
                    label={type.label}
                    icon={type.icon}
                    sx={{ backgroundColor: `${tertColor}22` }}
                  />
                ))}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography
              variant="h6"
              gutterBottom
              fontWeight="bold"
              color={tertColor}
            >
              Data Security & Compliance
            </Typography>

            <Typography variant="body1" paragraph>
              As a full-fledged U.S. law firm, we enforce strict client
              confidentiality to protect attorney-client privilege, attorney
              work product, and our ethical duty of confidentiality. This unique
              positioning makes us an ideal partner for sensitive legal AI data
              annotation projects, particularly those involving proprietary
              legal strategies or confidential business information.
            </Typography>

            <Box sx={{ mt: 3, mb: 4 }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {securityFeatures.map((feature, index) => (
                  <Chip
                    key={index}
                    label={feature.label}
                    icon={feature.icon}
                    sx={{
                      backgroundColor: "#f0f7ff",
                      border: "1px solid #90caf9",
                    }}
                  />
                ))}
              </Box>
            </Box>

            <Card
              elevation={2}
              sx={{
                backgroundColor: "#f9f9f9",
                borderRadius: 2,
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom color={tertColor}>
                  Staffing Locations
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Chip
                        label="Phoenix, USA"
                        avatar={
                          <Avatar
                            src={getImageUrl("usa_flag_round_100px.png")}
                          />
                        }
                        sx={{ mr: 2 }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Our U.S. team provides legal expertise, strategic
                      oversight, and quality control for all annotation
                      projects.
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Chip
                        label="Nairobi, Kenya"
                        avatar={<Avatar src={getImageUrl("kenya_flag.png")} />}
                        sx={{ mr: 2 }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Our Nairobi team includes engineers and legal
                      professionals who provide cost-effective scaling for
                      larger annotation projects.
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        <Box>
          <Typography
            variant="h6"
            gutterBottom
            fontWeight="bold"
            color={tertColor}
          >
            Industry Applications
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Typography variant="body1" paragraph>
                Our legal data annotation and prompt engineering services have
                been successfully deployed across various legal AI applications,
                with particular expertise in the following areas:
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      height: "100%",
                      backgroundColor: "#f9f9f9",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Patent Analytics
                    </Typography>
                    <Typography variant="body2">
                      Training AI models to analyze patent claims, identify
                      prior art, and assess patent strength and infringement
                      risks.
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      height: "100%",
                      backgroundColor: "#f9f9f9",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Contract Analysis
                    </Typography>
                    <Typography variant="body2">
                      Developing AI systems capable of extracting, analyzing,
                      and comparing key provisions across complex commercial
                      agreements.
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      height: "100%",
                      backgroundColor: "#f9f9f9",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Legal Research Assistance
                    </Typography>
                    <Typography variant="body2">
                      Training models to identify relevant case law, statutes,
                      and regulations for specific legal questions or fact
                      patterns.
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      height: "100%",
                      backgroundColor: "#f9f9f9",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      Regulatory Compliance
                    </Typography>
                    <Typography variant="body2">
                      Developing AI tools for identifying compliance issues in
                      corporate documents and procedures across various
                      regulatory frameworks.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  p: 3,
                  backgroundColor: "#f0f7ff",
                  borderRadius: 2,
                  border: `1px solid ${tertColor}33`,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  fontWeight="bold"
                  color={tertColor}
                >
                  Ready to Enhance Your Legal AI Tools?
                </Typography>

                <Typography variant="body1" paragraph>
                  Our combination of deep technical understanding and legal
                  expertise allows us to deliver nuanced, secure, and precise AI
                  data solutions that enhance your legal AI tools' accuracy and
                  effectiveness.
                </Typography>

                <Typography variant="body1" paragraph>
                  Whether you're developing new AI legal tools or looking to
                  improve existing ones, our specialized data annotation and
                  prompt engineering services can help you achieve superior
                  results.
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <Button
                    startIcon={<PhoneInTalkIcon />}
                    variant="contained"
                    onClick={() => scrollToSection("contact-section")}
                    sx={{
                      backgroundColor: tertColor,
                      color: "white",
                      "&:hover": {
                        color: "white",
                        backgroundColor: tertHoverColor,
                      },
                    }}
                  >
                    Schedule a Consultation
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default AiDataAnnotation;
